"use client";

import { MapPin } from "lucide-react";
import type { ReactNode } from "react";
import { EventCardMediaClient } from "@/components/event-card/event-card-media";
import { AppImage as Image } from "@/components/ui/app-image";
import type { MediaData } from "@/db/schema/hiive";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";
import type { MusicAttachment } from "@/lib/music/attachment";
import { cn } from "@/lib/utils";

interface Props {
  /** The class's own gallery, in display order. Video plays (muted if forced). */
  media: MediaData[];
  /** For the media's accessible name: "Hatha at Mahati Wellness". */
  label: string;
  hiiveName: string;
  hiiveLogoUrl: string | null;
  venueName: string;
  /** A poster URL — never a video binary. */
  venueImageUrl: string | null;
  /** Under the venue name: "7.0 km away", an address. */
  venueSubtitle?: string | null;
  /** Extra glass badges beside the hiive logo ("Members"). */
  badges?: ReactNode;
  /** Shown when there is no media at all (the activity's icon). */
  fallback?: ReactNode;
  size?: "default" | "large";
  forceMuted?: boolean;
  priority?: boolean;
  /** Banner song over a still lead image (hidden on force-muted tiles). */
  music?: MusicAttachment | null;
  className?: string;
}

/**
 * A venue class's picture, as every surface that names a class shows it: its
 * own media at 4:5, the hiive's logo on glass top-left, and the venue — its
 * 16:10 thumbnail and name — bottom-left, where an event card prints its
 * location.
 *
 * Lifted out of the "Open to book" card so the checkout shows the class that
 * was booked the same way the card that sold it did. The checkout used to
 * show the venue's banner under the class's name.
 */
export function ExperienceMediaFrame({
  media,
  label,
  hiiveName,
  hiiveLogoUrl,
  venueName,
  venueImageUrl,
  venueSubtitle,
  badges,
  fallback,
  size = "default",
  forceMuted = false,
  music = null,
  priority = false,
  className,
}: Props) {
  const isLarge = size === "large";
  const badgeSize = isLarge ? "h-8 w-8" : "h-6 w-6";
  // The venue's banner is framed 16:10, so its thumbnail is too — a square
  // crop of a landscape shot of a room is a close-up of the middle of a wall.
  const venueThumbWidth = isLarge ? "w-12" : "w-9";

  return (
    <div
      className={cn(
        "relative w-full shrink-0 overflow-hidden bg-surface-alt dark:bg-white/[0.06]",
        ASPECT_RATIO_CLASS.portrait4By5,
        className,
      )}
    >
      {media.length > 0 ? (
        <EventCardMediaClient
          media={media}
          eventName={label}
          forceMuted={forceMuted}
          priority={priority}
          volumeButtonPosition="top-right"
          music={forceMuted ? null : music}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center text-text-tertiary dark:text-white/42">
          {fallback ?? (
            <MapPin
              className={isLarge ? "h-7 w-7" : "h-5 w-5"}
              aria-hidden="true"
            />
          )}
        </div>
      )}

      {/* Top scrim, for the badges. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-2/5 bg-gradient-to-b from-black/40 to-transparent"
        aria-hidden="true"
      />

      <div
        className={cn(
          "absolute z-20 flex items-center gap-1.5",
          isLarge ? "top-3 left-3" : "top-2 left-2",
        )}
      >
        {hiiveLogoUrl ? (
          <div
            className={cn(
              "relative overflow-hidden rounded-lg border border-glass-border-10 bg-[rgba(0,0,0,0.45)] backdrop-blur-md",
              badgeSize,
            )}
          >
            <Image
              src={hiiveLogoUrl}
              alt={`${hiiveName} logo`}
              fill
              className="object-cover"
              sizes="32px"
            />
          </div>
        ) : null}
        {badges}
      </div>

      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center bg-gradient-to-t from-black/75 via-black/35 to-transparent",
          isLarge ? "gap-2 px-3 pt-10 pb-3" : "gap-1.5 px-2 pt-8 pb-2",
        )}
      >
        <div
          className={cn(
            "relative flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/25 bg-[rgba(0,0,0,0.45)] text-white/80 shadow-[0_2px_8px_rgba(0,0,0,0.35)] backdrop-blur-md",
            ASPECT_RATIO_CLASS.venueBooking,
            venueThumbWidth,
          )}
        >
          {venueImageUrl ? (
            <Image
              src={venueImageUrl}
              alt=""
              fill
              className="object-cover"
              sizes="48px"
            />
          ) : (
            <MapPin
              className={isLarge ? "h-3.5 w-3.5" : "h-3 w-3"}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <p
            className={cn(
              "truncate font-semibold text-white",
              isLarge ? "text-[12px]" : "text-[10px]",
            )}
          >
            {venueName}
          </p>
          {venueSubtitle ? (
            <p
              className={cn(
                "truncate text-white/70",
                isLarge ? "text-[11px]" : "text-[9px]",
              )}
            >
              {venueSubtitle}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
